import React, { useState, useEffect, useRef, useCallback } from "react";
import {
  Box,
  MenuItem,
  Select,
  IconButton,
  Tooltip,
  useMediaQuery,
  Button,
  FormControl,
  InputLabel,
  Divider,
  Typography,
  Snackbar,
  Alert,
  Fab,
  Collapse,
  TextField,
  Autocomplete,
  Paper,
  InputAdornment,
  useTheme,
} from "@mui/material";
import {
  Add,
  Remove,
  SwapVert,
  GpsFixed,
  ZoomIn,
  KeyboardArrowUp,
  KeyboardArrowDown,
  FilterList,
  Search,
  LocationOn,
  Flag,
} from "@mui/icons-material";
import { useSpring, animated } from "react-spring";
import { useDrag, useGesture } from "react-use-gesture";
import BackWithBreadcrumbAppbar from "../components/BackWithBreadcrumbAppbar";
import { ReactComponent as Floorplan } from "../Assets/floorplannew.svg";

const SVGViewer = React.memo(
  React.forwardRef(
    (
      {
        scale,
        setScale,
        offset,
        setOffset,
        labels,
        fromLabel,
        toLabel,
        containerRef,
      },
      ref
    ) => {
      const [dimensions, setDimensions] = useState({ width: 0, height: 0 });
      const isMobile = useMediaQuery("(max-width:600px)");
      const svgRef = useRef(null);
      const [isDragging, setIsDragging] = useState(false);
      const [svgReady, setSvgReady] = useState(false);
      // console.log("svgReady", svgReady);
      // console.log("svgRef", svgRef);
      // console.log("ref", ref.current);

      const setRefs = useCallback(
        (node) => {
          if (typeof ref === "function") ref(node);
          else if (ref) ref.current = node;

          if (node) {
            // Check if the node itself is an SVG element
            if (node instanceof SVGElement) {
              svgRef.current = node;
              setSvgReady(true);
              const viewBox = node.getAttribute("viewBox");
              if (viewBox) {
                const [, , vbWidth, vbHeight] = viewBox
                  .split(/\s+|,/)
                  .map(Number);
                setDimensions({ width: vbWidth, height: vbHeight });
              }
            } else {
              // Otherwise, look for SVG element inside the node
              const svgElement = node.querySelector("svg");
              if (svgElement) {
                svgRef.current = svgElement;
                setSvgReady(true);
                const viewBox = svgElement.getAttribute("viewBox");
                if (viewBox) {
                  const [, , vbWidth, vbHeight] = viewBox
                    .split(/\s+|,/)
                    .map(Number);
                  setDimensions({ width: vbWidth, height: vbHeight });
                }
              } else {
                // Set up MutationObserver if SVG isn't immediately available
                const observer = new MutationObserver(() => {
                  const svg = node.querySelector("svg");
                  if (svg) {
                    svgRef.current = svg;
                    setSvgReady(true);
                    const viewBox = svg.getAttribute("viewBox");
                    if (viewBox) {
                      const [, , vbWidth, vbHeight] = viewBox
                        .split(/\s+|,/)
                        .map(Number);
                      setDimensions({ width: vbWidth, height: vbHeight });
                    }
                    observer.disconnect();
                  }
                });
                observer.observe(node, { childList: true, subtree: true });
              }
            }
          }
        },
        [ref]
      );

      // Setup spring animation for smooth movement
      const [{ xy }, set] = useSpring(() => ({
        xy: offset,
        config: { mass: 1, tension: 280, friction: 60 },
      }));

      useEffect(() => {
        set({ xy: offset });
      }, [offset, set]);

      useEffect(() => {
        const svgElement = ref.current?.querySelector("svg");
        if (svgElement) {
          svgRef.current = svgElement;
          const viewBox = svgElement.getAttribute("viewBox");
          if (viewBox) {
            const [, , vbWidth, vbHeight] = viewBox.split(/\s+|,/).map(Number);
            setDimensions({ width: vbWidth, height: vbHeight });
          }
        }
      }, [ref]);

      // Fixed: Use react-use-gesture without useEffect for domTarget
      useDrag(
        ({ movement: [mx, my], first, last, active }) => {
          if (first) {
            setIsDragging(true);
            set({ xy: offset });
          }

          if (active) {
            const newOffset = [offset[0] + mx, offset[1] + my];
            set({ xy: newOffset });
          }

          if (last) {
            setIsDragging(false);
            setOffset([offset[0] + mx, offset[1] + my]);
          }
        },
        {
          domTarget: containerRef,
          eventOptions: { passive: false },
        }
      );

      // Fixed: Use react-use-gesture without useEffect for domTarget
      useGesture(
        {
          onPinch: ({ delta: [d], origin: [ox, oy], first, last }) => {
            if (first) {
              // Store the position under the pinch for better scaling
              const containerRect =
                containerRef.current.getBoundingClientRect();
              const pinchPointX = (ox - containerRect.left - offset[0]) / scale;
              const pinchPointY = (oy - containerRect.top - offset[1]) / scale;

              // Save this position to adjust during scaling
              if (svgRef.current) {
                svgRef.current.pinchPoint = { x: pinchPointX, y: pinchPointY };
              }
            }

            // Calculate new scale
            const newScale = Math.max(0.5, Math.min(5, scale + d * 0.01));

            // Adjust offset to keep pinch point stationary
            if (svgRef.current?.pinchPoint) {
              const { x, y } = svgRef.current.pinchPoint;
              const scaleDiff = newScale - scale;

              // Update offset to maintain pinch point position
              const newOffsetX = offset[0] - x * scaleDiff;
              const newOffsetY = offset[1] - y * scaleDiff;

              if (last) {
                setOffset([newOffsetX, newOffsetY]);
              } else {
                set({ xy: [newOffsetX, newOffsetY] });
              }
            }

            setScale(newScale);
          },
        },
        {
          domTarget: containerRef,
          eventOptions: { passive: false },
        }
      );

      useEffect(() => {
        if (!svgReady) return;

        const highlightLabel = (labelId, isFrom) => {
          try {
            if (!labelId) return;

            // 1. Sanitize label to match SVG ID format
            const sanitizedLabelId = labelId.replace(/[^a-zA-Z0-9-]/g, "");

            // 2. Get text element with exact transform
            const textEl = svgRef.current.querySelector(
              `#${CSS.escape(sanitizedLabelId)}`
            );
            if (!textEl) {
              console.warn(
                `Element not found for: ${labelId} (ID: ${sanitizedLabelId})`
              );
              return;
            }

            // 3. Directly parse transform attribute
            const transform = textEl.getAttribute("transform");
            if (!transform) {
              console.warn("No transform attribute found on text element");
              return;
            }

            // 4. Extract exact translate values
            const translateMatch = transform.match(
              /translate\(([\d.]+)[,\s]+([\d.]+)\)/
            );
            if (!translateMatch || translateMatch.length < 3) {
              console.warn("Invalid transform format:", transform);
              return;
            }

            // 5. Get precise coordinates from transform
            const [_, tx, ty] = translateMatch;
            const translateX = parseFloat(tx);
            const translateY = parseFloat(ty);

            // 6. Account for dx/dy offsets if present
            const dx = parseFloat(textEl.getAttribute("dx") || 0);
            const dy = parseFloat(textEl.getAttribute("dy") || 0);

            // 7. Calculate final coordinates
            const finalX = translateX + dx;
            const finalY = translateY + dy;

            // 8. Create circle with exact transform values
            const ns = "http://www.w3.org/2000/svg";
            const sanitizedLabel = labelId.replace(/ /g, "-");
            const circleId = `highlight-${sanitizedLabel}-${
              isFrom ? "from" : "to"
            }`;

            let circle =
              svgRef.current.querySelector(`#${CSS.escape(circleId)}`) ||
              document.createElementNS(ns, "circle");

            // Updated circle styling with more vibrant colors
            const fromColors = {
              fill: "rgba(128, 0, 255, 0.25)",
              stroke: "#8000ff",
              glow: "0 0 10px rgba(128, 0, 255, 0.8)",
            };

            const toColors = {
              fill: "rgba(255, 107, 185, 0.25)",
              stroke: "#ff57b6",
              glow: "0 0 10px rgba(255, 87, 182, 0.8)",
            };

            circle.setAttribute("id", circleId);
            circle.setAttribute("cx", finalX + 30);
            circle.setAttribute("cy", finalY);
            circle.setAttribute("r", "51.315625");
            circle.setAttribute(
              "fill",
              isFrom ? fromColors.fill : toColors.fill
            );
            circle.setAttribute(
              "stroke",
              isFrom ? fromColors.stroke : toColors.stroke
            );
            circle.setAttribute("stroke-width", "3");
            circle.setAttribute("stroke-dasharray", "4,2");
            circle.style.pointerEvents = "none";
            // Add glow effect with filter
            circle.setAttribute(
              "filter",
              `drop-shadow(${isFrom ? fromColors.glow : toColors.glow})`
            );

            if (!circle.parentNode) {
              svgRef.current.appendChild(circle);
            }
          } catch (error) {
            console.error("Error highlighting label:", error);
          }
        };

        // Clear previous highlights
        const circles = svgRef.current.querySelectorAll('[id^="highlight-"]');
        circles.forEach((circle) => circle.remove());

        // Apply new highlights
        highlightLabel(fromLabel, true);
        highlightLabel(toLabel, false);

        return () => {
          // Remove highlight circles
          const circles = svgRef.current.querySelectorAll('[id^="highlight-"]');
          circles.forEach((circle) => circle.remove());
        };
      }, [svgReady, fromLabel, toLabel]);

      return (
        <animated.div
          style={{
            transform: xy.to(
              (x, y) => `translate3d(${x}px, ${y}px, 0) scale(${scale})`
            ),
            transformOrigin: "0 0",
            cursor: isDragging ? "grabbing" : "grab",
            touchAction: "none",
          }}
        >
          <Box
            ref={setRefs}
            component={Floorplan}
            sx={{
              width: "100%",
              height: "auto",
              filter: isMobile ? "contrast(1.1)" : "none",
            }}
          />
        </animated.div>
      );
    }
  )
);

const FloorPlanSVG = () => {
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState([0, 0]);
  const [labels, setLabels] = useState([]);
  const [stalls, setStalls] = useState([]);
  const [fromLabel, setFromLabel] = useState("");
  const [toLabel, setToLabel] = useState("");
  const [notification, setNotification] = useState({
    show: false,
    message: "",
    severity: "info",
  });
  const [controlsExpanded, setControlsExpanded] = useState(true);
  const svgRef = useRef(null);
  const containerRef = useRef(null);
  const navigatingRef = useRef(false);
  const isMobile = useMediaQuery("(max-width:600px)");
  const theme = useTheme();

  useEffect(() => {
    const extractLabels = () => {
      const textElements = svgRef.current?.querySelectorAll("text");
      if (textElements) {
        // First, collect all text elements
        const textData = Array.from(textElements).map((el) => ({
          id: el.id,
          text: el.textContent.trim(),
          transform: el.getAttribute("transform"),
        }));

        // Create a map of stall IDs to names
        const stallMap = new Map();

        // Process text elements to extract stall IDs and names
        textData.forEach((item) => {
          // Check if this is a stall ID (usually shorter text like E200)
          if (/^E\d+$/.test(item.text) || /^Entrance/.test(item.text)) {
            // Look for a matching name element with the same ID
            const nameElement = textData.find(
              (el) =>
                el.id === item.id &&
                el.text !== item.text &&
                !/^E\d+$/.test(el.text)
            );

            if (nameElement) {
              // Extract and clean up the name
              const name = nameElement.text.replace(/\n/g, " ").trim();
              stallMap.set(item.text, name);
            } else {
              // If no name found, use the ID as the name
              stallMap.set(item.text, item.text);
            }
          }
        });

        // Create stall objects for Autocomplete
        const stallObjects = Array.from(stallMap.entries()).map(
          ([id, name]) => ({
            id,
            name,
            category: id.startsWith("Entrance")
              ? "Entrances"
              : /^E\d+/.test(id)
              ? "Booths"
              : "Facilities",
          })
        );

        // Sort by category and then by name
        stallObjects.sort((a, b) => {
          if (a.category !== b.category) {
            return a.category.localeCompare(b.category);
          }
          return a.name.localeCompare(b.name);
        });

        setStalls(stallObjects);

        // Keep the original array of text content for backward compatibility
        const extracted = Array.from(textElements)
          .map((el) => el.textContent.trim())
          .filter((label) => label);
        setLabels([...new Set(extracted)].sort());
      }
    };

    if (svgRef.current) {
      extractLabels();
    }
  }, []);

  const handleResetView = () => {
    setScale(1);
    setOffset([0, 0]);
    setNotification({
      show: true,
      message: "View has been reset",
      severity: "success",
    });
  };
  const showBothLocations = useCallback(() => {
    if (!fromLabel || !toLabel || !svgRef.current || !containerRef.current)
      return;

    // Find elements using SVG DOM
    const fromElement = svgRef.current.querySelector(
      `#${CSS.escape(fromLabel.replace(/[^a-zA-Z0-9-]/g, ""))}`
    );
    const toElement = svgRef.current.querySelector(
      `#${CSS.escape(toLabel.replace(/[^a-zA-Z0-9-]/g, ""))}`
    );

    if (!fromElement || !toElement) return;

    try {
      // Get positions in SVG coordinates
      const fromTransform = fromElement.getAttribute("transform");
      const toTransform = toElement.getAttribute("transform");

      const parseTransform = (transform) => {
        const match = transform.match(/translate\(([\d.]+)[,\s]+([\d.]+)\)/);
        return match
          ? { x: parseFloat(match[1]), y: parseFloat(match[2]) }
          : { x: 0, y: 0 };
      };

      const fromPos = parseTransform(fromTransform);
      const toPos = parseTransform(toTransform);

      // Calculate bounding area with 20% padding
      const padding = 0.2; // 20% of viewport
      const minX = Math.min(fromPos.x, toPos.x);
      const minY = Math.min(fromPos.y, toPos.y);
      const maxX = Math.max(fromPos.x, toPos.x);
      const maxY = Math.max(fromPos.y, toPos.y);

      const width = maxX - minX;
      const height = maxY - minY;

      const viewport = containerRef.current.getBoundingClientRect();
      const scaleX = viewport.width / (width * (1 + padding));
      const scaleY = viewport.height / (height * (1 + padding));
      const newScale = Math.min(scaleX, scaleY, 3); // Max zoom 3x

      // Center the area
      const centerX = (minX + maxX) / 2;
      const centerY = (minY + maxY) / 2;

      // Calculate offset to center the view
      const newOffsetX = viewport.width / 2 - centerX * newScale;
      const newOffsetY = viewport.height / 2 - centerY * newScale;

      // Apply with smooth transition
      setScale(newScale);
      setOffset([newOffsetX, newOffsetY]);
    } catch (error) {
      console.error("Error showing locations:", error);
    }
  }, [fromLabel, toLabel]);
  // Completely rewritten navigation function with more direct positioning
  const handleNavigateToLabel = useCallback(
    (label) => {
      if (!label || !svgRef.current || !containerRef.current) return;

      // Prevent multiple navigations happening simultaneously
      if (navigatingRef.current) return;
      navigatingRef.current = true;

      try {
        // Instead of trying to center on the specific label, simply reset the view first
        setScale(1);
        setOffset([0, 0]);

        // Allow a moment for the reset to apply
        // setTimeout(() => {
        //   // After selection, check if both labels are set
        //   if (fromLabel && toLabel) {
        //     // showBothLocations();
        //   }
        // }, 50);
      } catch (error) {
        console.error("Navigation error:", error);
        setNotification({
          show: true,
          message: `Error selecting ${label}`,
          severity: "error",
        });
        navigatingRef.current = false;
      }
    },
    [fromLabel, toLabel, isMobile]
  );

  // Show both locations in view
  // const showBothLocations = useCallback(() => {
  //   if (!fromLabel || !toLabel || !svgRef.current || !containerRef.current)
  //     return;

  //   // Find both elements
  //   const fromElement = Array.from(
  //     svgRef.current.querySelectorAll("text")
  //   ).find((el) => el.textContent.trim() === fromLabel);

  //   const toElement = Array.from(svgRef.current.querySelectorAll("text")).find(
  //     (el) => el.textContent.trim() === toLabel
  //   );

  //   if (!fromElement || !toElement) return;

  //   try {
  //     // Get bounding boxes
  //     const fromBox = fromElement.getBBox();
  //     const toBox = toElement.getBBox();

  //     // Get container dimensions
  //     const containerRect = containerRef.current.getBoundingClientRect();
  //     const controlsHeight =
  //       isMobile && controlsExpanded ? containerRect.height * 0.3 : 0;
  //     const effectiveHeight = containerRect.height - controlsHeight;

  //     // Calculate the combined bounding box with padding
  //     const padding = 50; // Additional space around the elements
  //     const minX = Math.min(fromBox.x, toBox.x) - padding;
  //     const minY = Math.min(fromBox.y, toBox.y) - padding;
  //     const maxX =
  //       Math.max(fromBox.x + fromBox.width, toBox.x + toBox.width) + padding;
  //     const maxY =
  //       Math.max(fromBox.y + fromBox.height, toBox.y + toBox.height) + padding;

  //     // Calculate size of the combined area
  //     const areaWidth = maxX - minX;
  //     const areaHeight = maxY - minY;

  //     // Calculate center point of the area
  //     const areaCenterX = minX + areaWidth / 2;
  //     const areaCenterY = minY + areaHeight / 2;

  //     // Calculate required scale to fit the area
  //     let scaleX = containerRect.width / areaWidth;
  //     let scaleY = effectiveHeight / areaHeight;
  //     let newScale = Math.min(scaleX, scaleY) * 0.9; // 90% to add margin

  //     // Cap the scale within reasonable limits
  //     newScale = Math.max(0.7, Math.min(newScale, 3));

  //     // Calculate the offset to center the area
  //     const newOffsetX = containerRect.width / 2 - areaCenterX * newScale;
  //     const newOffsetY = effectiveHeight / 2 - areaCenterY * newScale;

  //     // Apply changes
  //     setScale(newScale);
  //     setTimeout(() => {
  //       setOffset([newOffsetX, newOffsetY]);
  //       setNotification({
  //         show: true,
  //         message: "Showing both locations",
  //         severity: "info",
  //       });
  //     }, 50);
  //   } catch (error) {
  //     console.error("Error showing both locations:", error);
  //   }
  // }, [fromLabel, toLabel]);

  // useEffect(() => {
  //   const handleResize = () => {
  //     if (fromLabel && toLabel) {
  //       // showBothLocations();
  //     }
  //   };

  //   window.addEventListener("resize", handleResize);
  //   return () => window.removeEventListener("resize", handleResize);
  // }, [fromLabel, toLabel]);
  // Update swap labels function
  const handleSwapLabels = () => {
    if (!fromLabel || !toLabel) return;

    const temp = fromLabel;
    setFromLabel(toLabel);
    setToLabel(temp);

    setNotification({
      show: true,
      message: "Start and end locations swapped",
      severity: "info",
    });

    // setTimeout(showBothLocations, 100);
  };

  // Show both locations when both are selected
  // useEffect(() => {
  //   if (fromLabel && toLabel) {
  //     // showBothLocations();
  //   }
  // }, [fromLabel, toLabel, showBothLocations]);

  // Categorize labels
  const categorizedLabels = labels.reduce(
    (acc, label) => {
      if (label?.startsWith("Entrance")) {
        acc.entrances.push(label);
      } else if (label && (/^E\d+/.test(label) || label.startsWith("E "))) {
        acc.booths.push(label);
      } else if (label) {
        acc.facilities.push(label);
      }
      return acc;
    },
    { entrances: [], booths: [], facilities: [] }
  );

  const renderMenuItems = (items) => {
    return items.map((label) => (
      <MenuItem key={label} value={label} sx={{ pl: 4 }}>
        {label}
      </MenuItem>
    ));
  };

  const handleCloseNotification = () => {
    setNotification({ ...notification, show: false });
  };

  const toggleControls = () => {
    setControlsExpanded(!controlsExpanded);
  };

  return (
    <>
      <BackWithBreadcrumbAppbar
        links={[{ label: "Dashboard", href: "/" }]}
        currentPage="FloorPlan"
      />

      <Box
        ref={containerRef}
        sx={{
          position: "relative",
          overflow: "hidden",
          height: "calc(100vh - 120px)",
          touchAction: "none",
          cursor: "grab",
          "&:active": { cursor: "grabbing" },
        }}
      >
        {/* Zoom Controls */}
        <Box
          sx={{
            position: "absolute",
            top: 16,
            right: 16,
            zIndex: 10,
            display: "flex",
            flexDirection: "row",
            gap: 1,
            background: theme.palette.background.paper,
            p: 1,
            borderRadius: 2,
            boxShadow: 2,
          }}
        >
          <Tooltip title="Zoom In">
            <IconButton onClick={() => setScale((s) => Math.min(s + 0.2, 5))}>
              <Add />
            </IconButton>
          </Tooltip>

          <Tooltip title="Zoom Out">
            <IconButton onClick={() => setScale((s) => Math.max(s - 0.2, 0.5))}>
              <Remove />
            </IconButton>
          </Tooltip>

          <Tooltip title="Reset View">
            <IconButton onClick={handleResetView}>
              <GpsFixed />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Location Legend - Simplified for mobile */}
        <Box
          sx={{
            position: "absolute",
            top: 16,
            left: 16,
            zIndex: 10,
            background: theme.palette.background.paper,
            p: isMobile ? 1 : 1.5,
            borderRadius: 2,
            boxShadow: 2,
            display: "flex",
            flexDirection: "column",
            gap: 1,
            maxWidth: isMobile ? "50%" : "auto",
          }}
        >
          {fromLabel && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  background: "linear-gradient(45deg, #00e1b9, #39cbb3)",
                  boxShadow: "0 0 6px rgba(0, 225, 185, 0.7)",
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: "bold",
                  fontSize: isMobile ? "0.7rem" : "0.875rem",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                Location:{" "}
                <span style={{ color: "#00e1b9" }}>
                  {stalls.find((s) => s.id === fromLabel)?.name || fromLabel}
                </span>
              </Typography>
            </Box>
          )}

          {toLabel && (
            <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
              <Box
                sx={{
                  width: 10,
                  height: 10,
                  borderRadius: "50%",
                  background: "linear-gradient(45deg, #ff57b6, #ff89cf)",
                  boxShadow: "0 0 6px rgba(255, 87, 182, 0.7)",
                }}
              />
              <Typography
                variant="body2"
                sx={{
                  fontWeight: "bold",
                  fontSize: isMobile ? "0.7rem" : "0.875rem",
                  whiteSpace: "nowrap",
                  overflow: "hidden",
                  textOverflow: "ellipsis",
                }}
              >
                To:{" "}
                <span style={{ color: "#ff57b6" }}>
                  {stalls.find((s) => s.id === toLabel)?.name || toLabel}
                </span>
              </Typography>
            </Box>
          )}

          {!fromLabel && !toLabel && (
            <Typography
              variant="body2"
              sx={{
                fontStyle: "italic",
                color: "text.secondary",
                fontSize: isMobile ? "0.7rem" : "0.875rem",
              }}
            >
              Search for locations below
            </Typography>
          )}
        </Box>

        <SVGViewer
          scale={scale}
          setScale={setScale}
          offset={offset}
          setOffset={setOffset}
          labels={labels}
          fromLabel={fromLabel}
          toLabel={toLabel}
          containerRef={containerRef}
          ref={svgRef}
        />

        {/* Toggle button for mobile controls */}
        {isMobile && (
          <Fab
            color="primary"
            size="small"
            onClick={toggleControls}
            sx={{
              position: "absolute",
              bottom: controlsExpanded ? "auto" : 16,
              top: controlsExpanded ? "calc(100% - 120px)" : "auto",
              left: "50%",
              transform: "translateX(-50%)",
              zIndex: 20,
              boxShadow: 3,
              display: "none",
            }}
          >
            {controlsExpanded ? <KeyboardArrowDown /> : <FilterList />}
          </Fab>
        )}

        {/* Search Controls - Updated UI */}
        <Box
          sx={{
            position: "absolute",
            bottom: 0,
            left: 0,
            right: 0,
            zIndex: 10,
            background: theme.palette.background.paper,
            p: isMobile ? 2 : 3,
            pt: isMobile ? (controlsExpanded ? 2 : 0) : 3,
            borderRadius: isMobile ? "16px 16px 0 0" : 2,
            boxShadow: 3,
            maxHeight: isMobile ? (controlsExpanded ? "50%" : "40px") : "auto",
            overflow: "hidden",
            transition: "all 0.3s ease-in-out",
          }}
        >
          <Collapse in={true}>
            <Box
              display="grid"
              gap={isMobile ? 1 : 1}
              gridTemplateColumns={isMobile ? "1fr" : "1fr"}
            >
              {/* From Search */}
              <Autocomplete
                value={stalls.find((s) => s.id === fromLabel) || null}
                onChange={(event, newValue) => {
                  if (newValue) {
                    setFromLabel(newValue.id);
                    handleNavigateToLabel(newValue.id);
                  } else {
                    setFromLabel("");
                  }
                }}
                options={stalls}
                getOptionLabel={(option) => option.name}
                groupBy={(option) => option.category}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="Find Location"
                    fullWidth
                    size={isMobile ? "small" : "medium"}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <LocationOn color="success" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderColor: fromLabel ? "#00e1b9" : undefined,
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#00e1b9",
                          borderWidth: 2,
                        },
                      },
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Typography variant="body1">{option.name}</Typography>
                    <Typography
                      variant="caption"
                      // color="text.secondary"
                      sx={{ ml: 1 }}
                    >
                      {option.id}
                    </Typography>
                  </Box>
                )}
                PaperComponent={(props) => (
                  <Paper
                    {...props}
                    elevation={8}
                    sx={{
                      maxHeight: 300,
                      "&::-webkit-scrollbar": { width: 8 },
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "rgba(0,0,0,0.2)",
                        borderRadius: 4,
                      },
                    }}
                  />
                )}
              />

              {/* Swap Button */}
              {/* {(!isMobile || (fromLabel && toLabel)) && (
                <Box
                  sx={{
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    transform: isMobile ? "rotate(90deg)" : "none",
                  }}
                >
                  <IconButton
                    onClick={handleSwapLabels}
                    size={isMobile ? "small" : "large"}
                    disabled={!fromLabel || !toLabel}
                    sx={{
                      background:
                        fromLabel && toLabel
                          ? "rgba(0, 0, 0, 0.04)"
                          : undefined,
                      "&:hover": {
                        background:
                          fromLabel && toLabel
                            ? "rgba(0, 0, 0, 0.08)"
                            : undefined,
                      },
                    }}
                  >
                    <SwapVert fontSize={isMobile ? "small" : "large"} />
                  </IconButton>
                </Box>
              )} */}

              {/* To Search */}
              {/* <Autocomplete
                value={stalls.find((s) => s.id === toLabel) || null}
                onChange={(event, newValue) => {
                  if (newValue) {
                    setToLabel(newValue.id);
                    handleNavigateToLabel(newValue.id);
                  } else {
                    setToLabel("");
                  }
                }}
                options={stalls}
                getOptionLabel={(option) => option.name}
                groupBy={(option) => option.category}
                renderInput={(params) => (
                  <TextField
                    {...params}
                    label="To"
                    fullWidth
                    size={isMobile ? "small" : "medium"}
                    InputProps={{
                      ...params.InputProps,
                      startAdornment: (
                        <InputAdornment position="start">
                          <Flag color="error" />
                        </InputAdornment>
                      ),
                    }}
                    sx={{
                      "& .MuiOutlinedInput-root": {
                        borderColor: toLabel ? "#ff57b6" : undefined,
                        "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                          borderColor: "#ff57b6",
                          borderWidth: 2,
                        },
                      },
                    }}
                  />
                )}
                renderOption={(props, option) => (
                  <Box component="li" {...props}>
                    <Typography variant="body1">{option.name}</Typography>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ ml: 1 }}
                    >
                      {option.id}
                    </Typography>
                  </Box>
                )}
                PaperComponent={(props) => (
                  <Paper
                    {...props}
                    elevation={8}
                    sx={{
                      maxHeight: 300,
                      "&::-webkit-scrollbar": { width: 8 },
                      "&::-webkit-scrollbar-thumb": {
                        backgroundColor: "rgba(0,0,0,0.2)",
                        borderRadius: 4,
                      },
                    }}
                  />
                )}
              /> */}
            </Box>

            {/* Clear button - more compact on mobile */}
            <Button
              fullWidth
              variant="outlined"
              size={isMobile ? "small" : "medium"}
              sx={{
                mt: isMobile ? 1 : 2,
                fontWeight: "bold",
                py: isMobile ? 0.5 : 1,
              }}
              onClick={() => {
                setFromLabel("");
                setToLabel("");
                setNotification({
                  show: true,
                  message: "Selections cleared",
                  severity: "info",
                });
              }}
            >
              Clear Selections
            </Button>
          </Collapse>
        </Box>

        {/* Notification */}
        <Snackbar
          open={notification.show}
          autoHideDuration={3000}
          onClose={handleCloseNotification}
          anchorOrigin={{ vertical: "top", horizontal: "center" }}
        >
          <Alert
            onClose={handleCloseNotification}
            severity={notification.severity}
            sx={{ width: "100%" }}
          >
            {notification.message}
          </Alert>
        </Snackbar>
      </Box>
    </>
  );
};

export default React.memo(FloorPlanSVG);
